import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import Owners from "./pages/Owners";
import Contacts from "./pages/Contacts";
import CRM from "./pages/CRM";
import CalendarPage from "./pages/CalendarPage";
import MapPage from "./pages/MapPage";
import Tags from "./pages/Tags";
import Settings from "./pages/Settings";
import PropertyCompare from "./pages/PropertyCompare";
import TasksPage from "./pages/TasksPage";
import Investors from "./pages/Investors";
import InvestorDetail from "./pages/InvestorDetail";
import DealRoomPublic from "./pages/DealRoomPublic";
import ImportsPage from "./pages/ImportsPage";
import AdminAnalytics from "./pages/AdminAnalytics";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-muted-foreground" data-testid="auth-loading">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Blocca l'accesso alle pagine interne per il ruolo cliente.
function AgentOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/r/:token" element={<DealRoomPublic />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="properties" element={<Properties />} />
        <Route path="properties/:id" element={<PropertyDetail />} />
        <Route path="compare" element={<PropertyCompare />} />
        <Route path="owners" element={<AgentOnlyRoute><Owners /></AgentOnlyRoute>} />
        <Route path="contacts" element={<AgentOnlyRoute><Contacts /></AgentOnlyRoute>} />
        <Route path="investors" element={<AgentOnlyRoute><Investors /></AgentOnlyRoute>} />
        <Route path="investors/:id" element={<AgentOnlyRoute><InvestorDetail /></AgentOnlyRoute>} />
        <Route path="crm" element={<AgentOnlyRoute><CRM /></AgentOnlyRoute>} />
        <Route path="map" element={<AgentOnlyRoute><MapPage /></AgentOnlyRoute>} />
        <Route path="calendar" element={<AgentOnlyRoute><CalendarPage /></AgentOnlyRoute>} />
        <Route path="tasks" element={<AgentOnlyRoute><TasksPage /></AgentOnlyRoute>} />
        <Route path="tags" element={<AgentOnlyRoute><Tags /></AgentOnlyRoute>} />
        <Route path="imports" element={<AgentOnlyRoute><ImportsPage /></AgentOnlyRoute>} />
        <Route path="admin/analytics" element={<AgentOnlyRoute><AdminAnalytics /></AgentOnlyRoute>} />
        <Route path="settings" element={<AgentOnlyRoute><Settings /></AgentOnlyRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="bottom-right" richColors closeButton />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
